import type PicklistsService from '@efdigital/ll-addon-content/services/picklists';
import type { PicklistItem } from '@efdigital/ll-addon-data';
import { selectValue } from '@efdigital/ll-addon-ui/modifiers/select-value';
import { queryPicklistItemsByGroupName } from '@efdigital/ll-admin-v2/data/builders/system/picklist-items';
import { uniqueId } from '@ember/helper';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { ReactiveDataDocument } from '@warp-drive/core/reactive';
import { withReactiveResponse } from '@warp-drive/core/request';
import { Request } from '@warp-drive/ember';

interface PicklistItemPickerSignature {
  Args: {
    label: string;
    group: string;
    name: string;
    selected: string;
    selectedIsTitle?: boolean;
    readonly: boolean;
    allowBlank?: boolean;
    blankLabel?: string;
    onSelect?: (item: PicklistItem | null) => void;
  };
  Element: HTMLDivElement;
}

export class PicklistItemPicker extends Component<PicklistItemPickerSignature> {
  @service declare readonly picklists: PicklistsService;

  queryPicklistItems = (groupName: string) => {
    return withReactiveResponse<PicklistItem[]>(
      queryPicklistItemsByGroupName(groupName)
    );
  };

  itemSelected = (
    items: ReactiveDataDocument<PicklistItem[]>,
    event: Event
  ) => {
    const selectedKey = (event.target as HTMLSelectElement).value;
    if (selectedKey === '') {
      this.args.onSelect?.(null);
      return;
    }
    const item = items.data.find((item) => item.key === selectedKey);
    if (item) {
      this.args.onSelect?.(item);
    }
  };

  selectedKeyFrom = (items: ReactiveDataDocument<PicklistItem[]>): string => {
    const selected = this.args.selected;
    return this.args.selectedIsTitle
      ? items.data.find((item) => item.title === selected)?.key || ''
      : selected;
  };

  get required(): boolean {
    return this.args.allowBlank !== true;
  }

  <template>
    {{#let (uniqueId) as |uniqueId|}}
      <div class="ll-picklist-item-picker" ...attributes>
        <label for={{uniqueId}}>{{@label}}</label>
        <Request @query={{this.queryPicklistItems @group}}>
          <:content as |items|>
            <select
              id={{uniqueId}}
              name={{@name}}
              class="form-control"
              {{selectValue (this.selectedKeyFrom items)}}
              {{on "change" (fn this.itemSelected items)}}
              disabled={{@readonly}}
              required={{this.required}}
            >
              <option value="">{{if
                  @blankLabel
                  @blankLabel
                  "Select..."
                }}</option>
              {{#each items.data as |item|}}
                <option value={{item.key}}>{{item.title}}</option>
              {{/each}}
            </select>
          </:content>
          <:loading>
            {{! template-lint-disable no-duplicate-id }}
            <select id={{uniqueId}} class="form-control" disabled>
              <option value="">Loading...</option>
            </select>
          </:loading>
        </Request>
      </div>
    {{/let}}
  </template>
}
